// Redesigned pagination footer with page numbers and prev/next arrows
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';

const TablePagination = ({ activePage, totalPages, totalRecords, pageSize, onPageClick }) => {
  const startRecord = ((activePage - 1) * pageSize) + 1;
  const endRecord = Math.min(activePage * pageSize, totalRecords);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, activePage - 1);
      let end = Math.min(totalPages - 1, activePage + 1);

      if (activePage <= 3) { start = 2; end = 4; }
      if (activePage >= totalPages - 2) { start = totalPages - 3; end = totalPages - 1; }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  if (totalPages <= 0) return null;

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
      <span className="tw-text-sm tw-text-muted-foreground">
        Showing {startRecord}-{endRecord} of {totalRecords} members
      </span>
      {totalPages > 1 && (
        <div className="tw-flex tw-items-center tw-gap-1">
          <button
            onClick={() => activePage > 1 && onPageClick(activePage - 1)}
            disabled={activePage <= 1}
            className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-border tw-border-input tw-bg-background tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-accent"
            aria-label="Previous page"
          >
            <ChevronLeft className="tw-h-4 tw-w-4" />
          </button>

          {getPageNumbers().map((page, idx) =>
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="tw-px-2 tw-text-sm tw-text-muted-foreground">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageClick(page)}
                className={cn(
                  'tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-text-sm tw-font-medium tw-cursor-pointer tw-border-0',
                  page === activePage
                    ? 'tw-bg-primary tw-text-primary-foreground'
                    : 'tw-bg-background hover:tw-bg-accent tw-text-foreground'
                )}
              >
                {page}
              </button>
            )
          )}

          <button
            onClick={() => activePage < totalPages && onPageClick(activePage + 1)}
            disabled={activePage >= totalPages}
            className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-border tw-border-input tw-bg-background tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-accent"
            aria-label="Next page"
          >
            <ChevronRight className="tw-h-4 tw-w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TablePagination;
